import React, { FC } from "react"
import { Link, Outlet } from "react-router-dom"
import { ErrorBoundary } from "react-error-boundary"

import {
    Box,
    Group,
    Anchor,
    Divider,
    Text,
    useMantineColorScheme,
    Stack,
    useMantineTheme,
    Drawer,
    Image,
    Title,
    Tooltip,
} from "@mantine/core"
import { useDisclosure, useMediaQuery } from "@mantine/hooks"
import {
    IconBoxMultiple,
    IconDeviceDesktopAnalytics,
    IconLogout,
    IconMenu2,
    IconMoonStars,
    IconRouter,
    IconSettings,
    IconSun,
    IconX,
} from "@tabler/icons-react"

import RequiresAuth from "../../../features/auth/components/require-auth/RequiresAuth"
import useSignOut from "./useSignOut"
import { useLanguage } from "../../../contexts/language-context"
import ErrorBoundaryCard from "../../fallbacks/ErrorBoundaryCard"
import EcomoniLogo from "../../../assets/logos/ecomoni_logo.svg"
import NavigationButton from "./NavigationButton"
import TransparentButton from "../../inputs/transparent-button/TransparentButton"
import { useAuth } from "../../../contexts/authentication-context"

type AppShellProps = {
    bleed?: number
    px?: number
}

const AppShell: FC<AppShellProps> = ({ bleed = 16 }) => {
    const signOut = useSignOut()
    const { user } = useAuth()
    const { colorScheme, toggleColorScheme } = useMantineColorScheme()
    const { L } = useLanguage()
    const theme = useMantineTheme()
    const mobileLg = useMediaQuery(`(max-width: ${theme.breakpoints.lg})`)
    const mobileMd = useMediaQuery(`(max-width: ${theme.breakpoints.md})`)
    const [opened, { open, close }] = useDisclosure(false)

    return (
        <RequiresAuth>
            <Stack mih="100svh" spacing={0}>
                {user?.isSuper && (
                    <Box bg={theme.colorScheme === "dark" ? "orange.3" : "orange.6"} p="0.25rem" top={0} pos="sticky">
                        <Text
                            size="xs"
                            weight="bold"
                            color={theme.colorScheme === "dark" ? "dark.7" : "gray.0"}
                            ta="center"
                        >
                            {L(
                                "You are currently viewing the dashboard as the %s organisation. %s",
                                user.organisation,
                                <Text component={Link} to="/settings" underline>
                                    {L("Change")}
                                </Text>,
                            )}
                        </Text>
                    </Box>
                )}
                <Group
                    py={{
                        base: "0.25rem",
                        md: "1rem",
                    }}
                    px={{
                        base: "1.5rem",
                        sm: "2rem",
                        md: "4rem",
                    }}
                    spacing="4rem"
                    noWrap
                    top={0}
                    pos="sticky"
                    bg={theme.fn.darken(theme.colors.green[2], 0.75)}
                    sx={{
                        zIndex: 10,
                        borderBottom: `2px solid ${theme.fn.rgba(theme.colors.gray[0], 0.15)}`,
                    }}
                >
                    {mobileMd ? (
                        <>
                            <Group w="100%" noWrap>
                                <Box h="2rem" w="2rem">
                                    <Image src={EcomoniLogo} />
                                </Box>
                                <Title order={3} color="gray.0" mr="auto">
                                    Ecomoni {import.meta.env.MODE === "development" ? "| DEV" : ""}
                                </Title>
                                <TransparentButton variant="icon" onClick={open}>
                                    <IconMenu2 size="1.25rem" />
                                </TransparentButton>
                            </Group>
                            <Drawer
                                opened={opened}
                                onClose={close}
                                size="18rem"
                                styles={{
                                    header: { display: "none" },
                                    body: {
                                        padding: 0,
                                        background: theme.fn.darken(theme.colors.green[2], 0.75),
                                    },
                                }}
                            >
                                <Stack h="100dvh" spacing={0}>
                                    <Group
                                        p="1.5rem"
                                        position="apart"
                                        noWrap
                                        sx={{
                                            borderBottom: `2px solid ${theme.fn.rgba(theme.colors.gray[0], 0.15)}`,
                                        }}
                                    >
                                        <Group>
                                            <Box h="2rem" w="2rem">
                                                <Image src={EcomoniLogo} />
                                            </Box>
                                            <Title order={3} color="gray.0">
                                                Ecomoni {import.meta.env.MODE === "development" ? "| DEV" : ""}
                                            </Title>
                                        </Group>
                                        <TransparentButton variant="icon" onClick={close}>
                                            <IconX size="1.25rem" />
                                        </TransparentButton>
                                    </Group>
                                    <Stack spacing="0.5rem" p="1rem" h="100%">
                                        <NavigationButton
                                            target="/"
                                            onClick={close}
                                            icon={IconDeviceDesktopAnalytics}
                                            position="left"
                                            w="100%"
                                        >
                                            {L("Dashboard")}
                                        </NavigationButton>
                                        <NavigationButton
                                            target="/devices"
                                            onClick={close}
                                            icon={IconRouter}
                                            position="left"
                                            w="100%"
                                        >
                                            {L("Devices")}
                                        </NavigationButton>
                                        <NavigationButton
                                            target="/groups"
                                            onClick={close}
                                            icon={IconBoxMultiple}
                                            position="left"
                                            w="100%"
                                        >
                                            {L("Groups")}
                                        </NavigationButton>
                                        <NavigationButton
                                            target="/settings"
                                            onClick={close}
                                            icon={IconSettings}
                                            position="left"
                                            h="3rem"
                                            w="100%"
                                        >
                                            {L("Settings")}
                                        </NavigationButton>
                                        <TransparentButton
                                            onClick={() => toggleColorScheme()}
                                            leftIcon={colorScheme === "dark" ? <IconSun /> : <IconMoonStars />}
                                            position="left"
                                            h="3rem"
                                            w="100%"
                                            mt="auto"
                                        >
                                            {colorScheme === "dark" ? L("Light theme") : L("Dark theme")}
                                        </TransparentButton>
                                        <TransparentButton
                                            onClick={signOut}
                                            leftIcon={<IconLogout />}
                                            position="left"
                                            h="3rem"
                                            w="100%"
                                        >
                                            {L("Sign out")}
                                        </TransparentButton>
                                    </Stack>
                                </Stack>
                            </Drawer>
                        </>
                    ) : (
                        <>
                            {mobileLg || (
                                <Group noWrap>
                                    <Box h="2rem" w="2rem">
                                        <Image src={EcomoniLogo} />
                                    </Box>
                                    <Title order={3} color="gray.0">
                                        Ecomoni {import.meta.env.MODE === "development" ? "| DEV" : ""}
                                    </Title>
                                </Group>
                            )}
                            <Group mr="auto" noWrap>
                                <NavigationButton target="/" icon={IconDeviceDesktopAnalytics}>
                                    {L("Dashboard")}
                                </NavigationButton>
                                <NavigationButton target="/devices" icon={IconRouter}>
                                    {L("Devices")}
                                </NavigationButton>
                                <NavigationButton target="/groups" icon={IconBoxMultiple}>
                                    {L("Groups")}
                                </NavigationButton>
                            </Group>
                            <Group>
                                <NavigationButton target="/settings" icon={IconSettings}>
                                    {L("Settings")}
                                </NavigationButton>
                                {mobileMd || (
                                    <Group>
                                        <Tooltip label={colorScheme === "dark" ? L("Light theme") : L("Dark theme")}>
                                            <div>
                                                <TransparentButton variant="icon" onClick={() => toggleColorScheme()}>
                                                    {colorScheme === "dark" ? <IconSun /> : <IconMoonStars />}
                                                </TransparentButton>
                                            </div>
                                        </Tooltip>
                                        <Tooltip label={L("Sign out")}>
                                            <div>
                                                <TransparentButton variant="icon" onClick={signOut}>
                                                    <IconLogout />
                                                </TransparentButton>
                                            </div>
                                        </Tooltip>
                                    </Group>
                                )}
                            </Group>
                        </>
                    )}
                </Group>
                <Box
                    h={`${bleed}rem`}
                    sx={theme => ({
                        background: theme.fn.darken(theme.colors.green[2], 0.75),
                    })}
                />
                <Box
                    mt={`-${bleed}rem`}
                    py="2rem"
                    px={{
                        base: "0.5rem",
                        xs: "2rem",
                        md: "4rem",
                    }}
                >
                    <ErrorBoundary fallback={<ErrorBoundaryCard />}>
                        <Outlet />
                    </ErrorBoundary>
                </Box>
                <Group position="center" m="xl" mt="auto">
                    <Text color="dimmed" size="sm">
                        © Ecomoni 2024
                    </Text>
                    <Divider orientation="vertical" />
                    <Anchor color="dimmed" size="sm" href={"https://ecomoni.nl/legal/terms"}>
                        {L("Terms and Conditions")}
                    </Anchor>
                    <Anchor color="dimmed" size="sm" href={"https://ecomoni.nl/legal/privacy"}>
                        {L("Privacy")}
                    </Anchor>
                </Group>
            </Stack>
        </RequiresAuth>
    )
}

export default AppShell
